/* eslint-disable max-lines */
import { UserRoundLinkSvg } from '@/assets/icons/user-round-link-svg';
import { offlinePatientLinkAtomFamily } from '@/atoms/offline-recording';
import { IS_SIDEBAR_PANEL_ENABLED } from '@/components/constants/flags';
import { RemoteSessionStatusIcon } from '@/components/layout/Navigation/sessions/side-nav-list/components/remote-session-status-icon';
import { useSidebarPanelOverlayLock } from '@/components/layout/Navigation/sidebar-panel/hooks/use-sidebar-panel-overlay-lock';
import {
  getSidebarPanelOverlayOpenProps,
  getSidebarPanelOverlayTriggerProps,
} from '@/components/layout/Navigation/sidebar-panel/sidebar-overlay-constants';
import { deleteSessionDataAtom } from '@/components/modal/delete-session-modal.atoms';
import EhrIcon from '@/components/ui/ehr-icon';
import { LinkSessionsComboBoxResponsive } from '@/components/ui/responsive-combobox/link-sessions-responsive-combobox';
import {
  statusIconColors,
  type statusIconVariants,
} from '@/components/ui/session-status/status.variants';
import { SessionUrgencyFlag } from '@/components/ui/session-urgency/session-urgency-flag';
import { isSessionUrgent } from '@/components/ui/session-urgency/types';
import {
  useIsFindOrLinkPatientEnabled,
  useIsSessionPrefetchEnabled,
} from '@/features/flags';
import { SessionListLinkPopover } from '@/features/patient-find-or-link/components/session-list-link-popover';
import { OfflineSyncIndicator } from '@/features/sessions/components/session-list/offline-sync-indicator';
import { PERMISSION_SUBJECTS } from '@/helpers/casl/ability';
import { isSessionPatientLinkingReadOnly } from '@/hooks/integrations/isSessionPatientLinkingReadOnly';
import { usePrefetchSessionById } from '@/hooks/query/useQuerySessionById';
import { useIsReviewingOtherUser } from '@/hooks/query/useQueryUser';
import useQueryUserTier from '@/hooks/query/useQueryUserTier';
import { useRBACPermissionCheck } from '@/hooks/useRBACPermissionCheck';
import { useTutorial } from '@/hooks/useTutorial';
import { useIsMobile } from '@/hooks/useWindowDimensions';
import { cn } from '@/lib/cn';
import { type RootState } from '@/store';
import { enumToDescription } from '@/utils/enumToDescription';
import { Avatar, AvatarFallback } from '@heidi/design-system/avatar';
import { buttonVariants } from '@heidi/design-system/button';
import { Checkbox } from '@heidi/design-system/checkbox';
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@heidi/design-system/hover-card';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import {
  TypographyCaption,
  TypographyFootNote,
  UserData,
} from '@heidi/design-system/typography';
import { type TranslationKey, useTranslations } from '@heidi/runtime/i18n';
import { startTracking, TrackingKey } from '@heidi/runtime/performance';
import { type EHRProvider, PatientSource, SessionStatus } from '@heidi/schema/ml-scribe';
import {
  getSessionListSummary,
  type SimpleSessionResponse,
} from '@heidi/session/contracts';
import * as HoverCardPrimitive from '@radix-ui/react-hover-card';
import { useAtomValue, useSetAtom } from 'jotai';
import { Circle, Trash2, Unlink } from 'lucide-react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, {
  forwardRef,
  memo,
  useCallback,
  useMemo,
  useState,
  useTransition,
} from 'react';
import { useSelector } from 'react-redux';
import LinkedSessionList from './linked-session-list';
type AccordionNavV2ButtonProps = {
  time?: string;
  onMobileSideBarDismiss?: () => void;
  onSessionsDeleted?: () => void;
  selected: boolean;
  onSelected: (selected: boolean) => void;
  showCheckbox?: boolean;
  onDeleteModalOpen?: () => void;
  isDeleteActionVisible?: boolean;
  session?: SimpleSessionResponse;
  integrationSwitchPatient?: (patientId: string) => void;
  shouldGoTutorialNextStep?: boolean;
};

const AccordionNavV3Button = forwardRef<HTMLAnchorElement, AccordionNavV2ButtonProps>(
  ({
    time,
    onMobileSideBarDismiss,
    onSessionsDeleted,
    selected,
    onSelected,
    showCheckbox,
    session,
    integrationSwitchPatient,
    shouldGoTutorialNextStep,
  }) => {
    const { t } = useTranslations();
    const [isTransitionPending, startTransition] = useTransition();
    const isSidebarPanelEnabled = IS_SIDEBAR_PANEL_ENABLED();
    const isFindOrLinkEnabled = useIsFindOrLinkPatientEnabled();
    const sidebarPanelOverlayTriggerProps =
      getSidebarPanelOverlayTriggerProps(isSidebarPanelEnabled);
    const [hoverCardOpen, setHoverCardOpen] = useState(false);
    const sidebarPanelOverlayOpenProps = getSidebarPanelOverlayOpenProps({
      isSidebarPanelEnabled,
      isOpen: hoverCardOpen,
    });

    useSidebarPanelOverlayLock(hoverCardOpen);

    const isMobile = useIsMobile();
    const isRunning = useSelector((state: RootState) => state.timerModel.isRunning);
    const isOfflineSyntheticSession = Boolean(session?.is_offline_synthetic);
    const isDeleteActionVisible = isMobile && !isRunning && !isOfflineSyntheticSession;
    const isReviewingOtherUser = useIsReviewingOtherUser();
    const { isTogetherOrHigherTier } = useQueryUserTier();
    const shouldApplySidebarStyles =
      !isReviewingOtherUser && !isDeleteActionVisible && !isOfflineSyntheticSession;

    const { checkRBACPermission } = useRBACPermissionCheck();
    const shouldShowLinkPatientProfile = useMemo(
      () =>
        checkRBACPermission(
          PERMISSION_SUBJECTS.TEAM_OTHERS_SESSION_EDIT,
          PERMISSION_SUBJECTS.SESSION_EDIT
        ),
      [checkRBACPermission]
    );
    const setDeleteSessionData = useSetAtom(deleteSessionDataAtom);

    const sessionId = useMemo(() => session?.session_id || '', [session]);
    const offlinePatientLink = useAtomValue(offlinePatientLinkAtomFamily(sessionId));
    const offlinePatientName = offlinePatientLink.trim();

    // `session_name` sits LAST, immediately before the untitled fallback, and
    // deliberately BELOW the gist — even though the consolidated list and the
    // session DataTable effectively rank it above. Promoting it here would
    // change the rendered title of every session that has both a name and a
    // gist, on every platform, which is a product change rather than a fix.
    // Read last, it only ever replaces a row that currently, wrongly, says
    // "Untitled session" while the same session reads correctly on web.
    //
    // It goes through the same displayable-name helper as the gist — as
    // `getRecentVisitLabelParts` already does with `session_name` — because a
    // raw read reintroduces the defect from the other side: a whitespace-only
    // name is truthy and would render as a blank title instead of falling
    // through to "Untitled session". The helper also drops the backend's
    // bookkeeping placeholders, which `session_name` can carry too.
    const title = useMemo(
      () =>
        session?.patient.demographic_details ||
        session?.patient.name ||
        offlinePatientName ||
        // Same rule as the session list and recent visits: the editable gist,
        // then the generated name, with a cleared summary resolving to untitled.
        getSessionListSummary(session ?? {}) ||
        t('shared.UntitledSession'),
      [offlinePatientName, session, t]
    );

    const isSessionLinked = useMemo(() => Boolean(session?.patient_profile), [session]);
    const isPatientLinkingReadOnly = useMemo(
      () =>
        isSessionPatientLinkingReadOnly({
          ehr_appt_id: session?.ehr_appt_id,
        }),
      [session]
    );
    const router = useRouter();
    const prefetchSessionById = usePrefetchSessionById();
    const isSessionPrefetchEnabled = useIsSessionPrefetchEnabled();
    const isActiveSession = useMemo(
      () => router.asPath.includes(sessionId),
      [router.asPath, sessionId]
    );

    const handleDeleteClick = useCallback(
      (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (isOfflineSyntheticSession) return;
        setDeleteSessionData({
          sessionsToDelete: [sessionId],
          selectedAll: false,
          onSessionsDeleted,
        });
      },
      [isOfflineSyntheticSession, onSessionsDeleted, setDeleteSessionData, sessionId]
    );

    const { onNextPage } = useTutorial();

    const patientInitials = useMemo(() => {
      const name = session?.patient_profile?.demographic_string?.trim();
      if (!name) {
        return undefined;
      }

      const parts = name.split(/\s+/);
      if (parts.length === 2) {
        const firstInitial = parts.at(0)?.charAt(0) ?? '';
        const secondInitial = parts.at(1)?.charAt(0) ?? '';
        return `${firstInitial}${secondInitial}`.toUpperCase();
      }
      return name.charAt(0).toUpperCase();
    }, [session]);

    const linkedSessionHoverCardContent = (
      <LinkedSessionHoverCard
        isSessionLinked={isSessionLinked}
        isMobile={isMobile}
        shouldShowLinkPatientProfile={shouldShowLinkPatientProfile}
        isPatientLinkingReadOnly={isPatientLinkingReadOnly}
        patientProfile={session?.patient_profile}
        onSessionsDeleted={onSessionsDeleted}
      />
    );

    return (
      <HoverCard
        {...(isSidebarPanelEnabled
          ? { open: hoverCardOpen, onOpenChange: setHoverCardOpen }
          : {})}
      >
        <HoverCardTrigger asChild>
          <Link
            href={`/scribe/session/${sessionId}`}
            key={sessionId}
            {...sidebarPanelOverlayTriggerProps}
            {...sidebarPanelOverlayOpenProps}
            onClick={(e) => {
              if (isTransitionPending) return;
              // Close HoverCard immediately before navigation starts to prevent
              // the portal from flashing to the top-left corner as the anchor unmounts.
              if (isSidebarPanelEnabled) setHoverCardOpen(false);
              if (isActiveSession) {
                e.preventDefault();
                e.stopPropagation();
              } else {
                startTracking({
                  key: TrackingKey.SCRIBE_SESSION_OPEN,
                  metadata: {
                    session_id: sessionId,
                    prefetchEnabled: isSessionPrefetchEnabled,
                  },
                });
                // Flag-gated: warm the session cache now so the GET overlaps
                // the route transition instead of firing after the page mounts.
                if (isSessionPrefetchEnabled && !isOfflineSyntheticSession) {
                  prefetchSessionById(sessionId);
                }
              }
              startTransition(() => {
                if (session?.ehr_patient_id && integrationSwitchPatient) {
                  integrationSwitchPatient(session.ehr_patient_id);
                }

                if (shouldGoTutorialNextStep) {
                  onNextPage();
                }

                if (onMobileSideBarDismiss) {
                  onMobileSideBarDismiss();
                }
              });
            }}
            tabIndex={0}
            aria-disabled={!isActiveSession}
          >
            <div
              data-active={isSidebarPanelEnabled ? isActiveSession : undefined}
              className={cn(
                buttonVariants({
                  variant: isSidebarPanelEnabled ? 'panel_ghost' : 'ghost',
                  size: 'xxs',
                  className: 'h-fit',
                }),
                'group relative w-full justify-between overflow-hidden ps-2',
                isActiveSession &&
                  !isSidebarPanelEnabled &&
                  'bg-surface-selected hover:bg-surface-selected-hover active:bg-surface-selected-pressed',
                shouldApplySidebarStyles &&
                  (isSidebarPanelEnabled
                    ? 'transition-none'
                    : 'transition-all duration-200 ease-in-out'),
                // Logical (`pe-`), not physical (`pr-`): the checkbox this
                // gutter reserves space for is positioned with `end-1.5`, so
                // under `dir="rtl"` a physical gutter reserves the wrong side
                // and the checkbox lands on top of the trailing status glyphs.
                shouldApplySidebarStyles &&
                  (selected || showCheckbox ? 'pe-7' : 'focus-within:pe-7 hover:pe-7')
              )}
              title={title}
            >
              {!isDeleteActionVisible &&
                !isReviewingOtherUser &&
                !isOfflineSyntheticSession && (
                  <div
                    className={cn(
                      'absolute end-1.5 flex h-full flex-row items-center opacity-0',
                      isSidebarPanelEnabled
                        ? 'transition-none'
                        : 'transition-all delay-100 duration-200 ease-in-out',
                      (selected || showCheckbox) && 'opacity-100',
                      !(selected || showCheckbox) &&
                        'group-focus-within:opacity-100 group-focus-within:transition-none group-hover:opacity-100'
                    )}
                  >
                    <Checkbox
                      checked={selected}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onSelected(!selected);
                      }}
                    />
                  </div>
                )}

              {/* Mobile view */}
              {isMobile ? (
                <div className={cn('flex flex-auto flex-col overflow-hidden')}>
                  {/* `max-w-full` keeps the shrink-to-fit row inside the
                      overflow-hidden column, so a long title truncates instead
                      of pushing the trailing status glyphs out of view. */}
                  <UserData className="flex w-fit max-w-full items-center gap-1">
                    <SessionUrgencyFlag isUrgent={isSessionUrgent(session)} />
                    {isSessionLinked &&
                      (session?.patient_profile?.source === PatientSource.EHR ? (
                        <div className="size-[18px]">
                          <EhrIcon
                            ehrProvider={
                              session?.patient_profile?.ehr as EHRProvider | null
                            }
                            iconSize={18}
                            iconClassName="text-text-secondary"
                          />
                        </div>
                      ) : (
                        <div className="size-[18px]">
                          <UserRoundLinkSvg
                            alt={t('patient.linkedUser')}
                            width={18}
                            height={18}
                          />
                        </div>
                      ))}
                    <TypographyCaption className="truncate">{title}</TypographyCaption>
                    {/* Offline sync status. Two things worth knowing here (the
                        desktop branch below repeats the markup, not the prose):

                        1. Flag gate. Enforced inside the indicator itself.
                           The data layer covers the queue-derived statuses
                           (`offline_sync_status` is only populated by
                           `unionOfflineSessions`, which no-ops when the flag is
                           off), but the indicator also resolves a note failure
                           straight from durable storage, which that gate never
                           sees. So the indicator now calls
                           `useIsOfflineRecordingEnabled()` directly and a gate
                           here would be redundant.
                        2. Icon-only (`hideLabel`) because the sidebar row is
                           narrow — the inline "Syncing…" / "Synced" labels
                           would compete with the title for width. Matches the
                           new session list's row treatment. */}
                    <OfflineSyncIndicator
                      status={session?.offline_sync_status}
                      sessionId={session?.session_id}
                      hideLabel
                      data-testid="sidebar-session-offline-sync-indicator"
                    />
                    <RemoteSessionStatusIcon sessionId={session?.session_id} />
                  </UserData>
                  <div className="flex items-center gap-1">
                    {isTogetherOrHigherTier && (
                      <Circle
                        fill="currentColor"
                        className={
                          statusIconColors[
                            session?.session_status?.toLowerCase() as keyof typeof statusIconVariants
                          ]
                        }
                        size={6}
                      />
                    )}
                    <TypographyFootNote
                      className={cn(
                        'truncate',
                        !isSidebarPanelEnabled &&
                          cn(
                            'text-text-secondary duration-200',
                            isActiveSession && 'text-text-primary'
                          )
                      )}
                    >
                      {time}
                    </TypographyFootNote>
                  </div>
                </div>
              ) : (
                /* Desktop view */
                <div className="flex w-full items-center gap-2">
                  <SessionUrgencyFlag isUrgent={isSessionUrgent(session)} />
                  {isFindOrLinkEnabled ? (
                    // Find-or-link path: mirrors the consolidated-list
                    // pattern. Whole Avatar passed as the popover trigger,
                    // wrapper div eats click/keydown bubble so the row's
                    // <Link> doesn't navigate. preventDefault on click is
                    // required here (and not on consolidated) because the
                    // parent is an <a>, not a <div role="button">. Gates
                    // only on `isSessionLinked` — BE enforces permission +
                    // EHR-readonly on the mutation.
                    <div
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                      onKeyDown={(e) => {
                        // Only stopPropagation — preventDefault here would
                        // block space/enter from reaching the popover's
                        // search input (portaled but still bubbles through
                        // the React tree). The popover's own trigger handles
                        // open-on-enter; we just keep the parent <Link>'s
                        // keyboard activation from firing.
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.stopPropagation();
                        }
                      }}
                    >
                      <SessionListLinkPopover
                        trigger={
                          <Avatar
                            {...sidebarPanelOverlayTriggerProps}
                            className={cn(
                              'size-6 border-[0.75px]',
                              isSessionLinked
                                ? 'border-border-selected text-text-primary'
                                : 'border-text-tertiary text-text-tertiary hover:border-border-selected hover:text-text-primary'
                            )}
                          >
                            <AvatarFallback
                              className={cn(
                                'flex items-center justify-center bg-transparent'
                              )}
                            >
                              {isSessionLinked ? (
                                <TypographyCaption className="leading-none">
                                  {patientInitials}
                                </TypographyCaption>
                              ) : (
                                <Unlink size={12} />
                              )}
                            </AvatarFallback>
                          </Avatar>
                        }
                        tooltipContent={t('patient.linkSessionToPatient')}
                        popoverAlign="start"
                        popoverSide="right"
                        popoverAlignOffset={-2}
                        popoverSideOffset={4}
                        commandInputPlaceholder={t('patient.searchForPatient')}
                        selectedSessionIds={[sessionId]}
                        patientProfileAppointmentCandidates={
                          session?.patient_profile_appointment_candidates
                        }
                        // Mirror the legacy branch's three-way gate so EHR
                        // readonly / no-permission users don't see an
                        // interactive avatar that fails on click — server
                        // would reject the link, surface as silent error.
                        showTooltip={
                          !isOfflineSyntheticSession &&
                          !isSessionLinked &&
                          shouldShowLinkPatientProfile
                        }
                        disabled={
                          isSessionLinked ||
                          !shouldShowLinkPatientProfile ||
                          isPatientLinkingReadOnly ||
                          isOfflineSyntheticSession
                        }
                      />
                    </div>
                  ) : (
                    // Legacy combobox path: original prod structure
                    // preserved exactly. Avatar owns the click handler;
                    // AvatarFallback is the popover trigger via asChild.
                    <Avatar
                      className={cn(
                        'size-6 border-[0.75px]',
                        isSessionLinked
                          ? 'border-border-selected text-text-primary'
                          : 'border-text-tertiary text-text-tertiary hover:border-border-selected hover:text-text-primary'
                      )}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      <LinkSessionsComboBoxResponsive
                        trigger={
                          <AvatarFallback
                            {...sidebarPanelOverlayTriggerProps}
                            className={cn(
                              'flex items-center justify-center bg-transparent'
                            )}
                          >
                            {isSessionLinked ? (
                              <TypographyCaption className="leading-none">
                                {patientInitials}
                              </TypographyCaption>
                            ) : (
                              <Unlink size={12} />
                            )}
                          </AvatarFallback>
                        }
                        tooltipContent={t('patient.linkSessionToPatient')}
                        popoverAlign="start"
                        popoverSide="right"
                        popoverAlignOffset={-2}
                        popoverSideOffset={4}
                        commandInputPlaceholder={t('patient.searchForPatient')}
                        selectedSessionIds={[sessionId]}
                        patientProfileAppointmentCandidates={
                          session?.patient_profile_appointment_candidates
                        }
                        showTooltip={
                          !isOfflineSyntheticSession &&
                          !isSessionLinked &&
                          shouldShowLinkPatientProfile
                        }
                        disabled={
                          isSessionLinked ||
                          !shouldShowLinkPatientProfile ||
                          isPatientLinkingReadOnly ||
                          isOfflineSyntheticSession
                        }
                      />
                    </Avatar>
                  )}
                  <div className={cn('flex flex-1 flex-col overflow-hidden')}>
                    <UserData className="flex w-fit max-w-full items-center gap-1">
                      {isSessionLinked &&
                        session?.patient_profile?.source === PatientSource.EHR && (
                          <div className="size-[12px]">
                            <EhrIcon
                              ehrProvider={
                                session?.patient_profile?.ehr as EHRProvider | null
                              }
                              iconSize={12}
                              iconClassName="text-text-secondary"
                            />
                          </div>
                        )}
                      <TypographyCaption className="max-w-full truncate">
                        {title}
                      </TypographyCaption>
                      {/* See the mobile branch above for the flag-gate and
                          icon-only rationale. */}
                      <OfflineSyncIndicator
                        status={session?.offline_sync_status}
                        sessionId={session?.session_id}
                        hideLabel
                        data-testid="sidebar-session-offline-sync-indicator"
                      />
                      <RemoteSessionStatusIcon sessionId={session?.session_id} />
                    </UserData>
                    <div className="flex items-center gap-1">
                      {isTogetherOrHigherTier && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div className="size-1.5">
                              <Circle
                                fill="currentColor"
                                className={cn(
                                  statusIconColors[
                                    session?.session_status?.toLowerCase() as keyof typeof statusIconVariants
                                  ],
                                  'max-h-full max-w-full'
                                )}
                              />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <TooltipArrow />
                            <TypographyCaption>
                              {t(
                                `sessionStatus.${enumToDescription[session?.session_status ?? SessionStatus.DRAFT]}` as TranslationKey
                              )}
                            </TypographyCaption>
                          </TooltipContent>
                        </Tooltip>
                      )}
                      <TypographyFootNote
                        className={cn(
                          'truncate',
                          !isSidebarPanelEnabled &&
                            cn(
                              'text-text-secondary duration-200',
                              isActiveSession && 'text-text-primary'
                            )
                        )}
                      >
                        {time}
                      </TypographyFootNote>
                    </div>
                  </div>
                </div>
              )}

              {/* Delete button */}
              {isDeleteActionVisible && isActiveSession && !isReviewingOtherUser && (
                <Trash2
                  size="18"
                  className="cursor-pointer text-validation-destructive duration-100 hover:text-validation-destructive-hover"
                  onClick={handleDeleteClick}
                />
              )}
            </div>
          </Link>
        </HoverCardTrigger>
        {/* HoverCardContent already includes its own Portal internally.
            When sidebar panel is disabled, keep the legacy outer Portal wrapper
            to avoid changing existing behaviour outside the feature flag. */}
        {isSidebarPanelEnabled ? (
          linkedSessionHoverCardContent
        ) : (
          <HoverCardPrimitive.Portal>
            {linkedSessionHoverCardContent}
          </HoverCardPrimitive.Portal>
        )}
      </HoverCard>
    );
  }
);

AccordionNavV3Button.displayName = 'AccordionNavV3Button';

const LinkedSessionHoverCard = memo(
  ({
    isSessionLinked,
    isMobile,
    shouldShowLinkPatientProfile,
    isPatientLinkingReadOnly,
    patientProfile,
    onSessionsDeleted,
  }: {
    isSessionLinked: boolean;
    isMobile: boolean;
    shouldShowLinkPatientProfile: boolean;
    isPatientLinkingReadOnly: boolean;
    patientProfile?: SimpleSessionResponse['patient_profile'];
    onSessionsDeleted?: () => void;
  }) => (
    <HoverCardContent side="right" className="z-999 w-fit rounded-sm p-0" align="start">
      {isSessionLinked && !isMobile && shouldShowLinkPatientProfile && (
        <LinkedSessionList
          patientProfile={patientProfile ?? undefined}
          forcePatientLinkingReadOnly={isPatientLinkingReadOnly}
          onSessionsDeleted={onSessionsDeleted}
        />
      )}
    </HoverCardContent>
  )
);
LinkedSessionHoverCard.displayName = 'LinkedSessionHoverCard';

export default memo(AccordionNavV3Button);
